// ZATCA Report — the signed-in branch's web Sales Invoices and Returns and where each
// one stands with ZATCA (API ZatcaController, DB script Section AQ): not signed yet,
// signed and waiting, reported / cleared, rejected, or not delivered.
// "Send" signs and sends one invoice now; "Send all pending" does the whole branch.
// POS bills are not here: the Windows POS reports those (see "ZTCA Status Report").
// Logic ported from the Inventory product's ZATCA Report; the layout follows this app.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { OverlayTrigger } from "react-bootstrap";
import { ChevronUp, RotateCcw } from "feather-icons-react/build/IconComponents";
import { Table } from "antd";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import LoadingUtils from "../../../core/utils/LoadingUtils";
import { setheadertoggle } from "../../../redux/toggle";
import { renderTooltip } from "../../../redux/helper";
import {
  downloadZatcaXml,
  getZatcaReport,
  resubmitZatca,
  zatcaError,
} from "../../../services/ZatcaApiServices";

const STATUSES = [
  { value: "ALL", label: "All" },
  { value: "PENDING", label: "Waiting to send" },
  { value: "UNSIGNED", label: "Not signed yet" },
  { value: "REPORTED", label: "Reported" },
  { value: "CLEARED", label: "Cleared" },
  { value: "REJECTED", label: "Rejected" },
  { value: "SEND_FAILED", label: "Not delivered" },
];

const STATUS_VIEW = {
  UNSIGNED: { label: "Not signed", tone: "bg-secondary" },
  SIGNED: { label: "Waiting", tone: "bg-warning text-dark" },
  SEND_FAILED: { label: "Not delivered", tone: "bg-warning text-dark" },
  REPORTED: { label: "Reported", tone: "bg-success" },
  CLEARED: { label: "Cleared", tone: "bg-success" },
  REJECTED: { label: "Rejected", tone: "bg-danger" },
};

const DOC_VIEW = {
  SIMSI: "Simplified invoice",
  STDSI: "Standard invoice",
  SIMCN: "Simplified credit note",
  STDCN: "Standard credit note",
};

const amt = (v) => Number(v || 0).toFixed(2);

const Stat = ({ label, value }) => (
  <div className="col-md-3 col-6">
    <div className="card mb-3">
      <div className="card-body py-3">
        <div className="text-muted small">{label}</div>
        <h4 className="mb-0">{value}</h4>
      </div>
    </div>
  </div>
);

const ZatcaReport = () => {
  const dispatch = useDispatch();
  const { branch } = useSelector((state) => state.branchlist);
  const { togglehead } = useSelector((state) => state.headerToggle);
  const [from, setFrom] = useState(dayjs().startOf("month").format("YYYY-MM-DD"));
  const [to, setTo] = useState(dayjs().format("YYYY-MM-DD"));
  const [status, setStatus] = useState("ALL");
  const [search, setSearch] = useState("");
  const [report, setReport] = useState({ rows: [] });
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    if (!branch?.guid || !from || !to) return;
    setLoading(true);
    try {
      setReport(await getZatcaReport(branch.guid, from, to, status));
    } catch (error) {
      toast.error(zatcaError(error, "Could not load the ZATCA report"));
      setReport({ rows: [] });
    } finally {
      setLoading(false);
    }
  }, [branch, from, to, status]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = report?.rows || [];
    return q
      ? list.filter(
          (r) =>
            `${r.entryNumber}`.toLowerCase().includes(q) ||
            `${r.customerName ?? ""}`.toLowerCase().includes(q) ||
            `${r.responseMsg ?? ""}`.toLowerCase().includes(q)
        )
      : list;
  }, [report, search]);

  const send = async (salesGuIDs) => {
    setSending(true);
    try {
      const r = await resubmitZatca(branch.guid, salesGuIDs);
      const parts = [];
      if (r.accepted) parts.push(`${r.accepted} accepted`);
      if (r.rejected) parts.push(`${r.rejected} rejected`);
      if (r.failed) parts.push(`${r.failed} not done`);
      const text = parts.length ? parts.join(", ") : r.sent ? `${r.sent} sent` : "Nothing was due";
      if (r.rejected || r.failed)
        toast.error(`${text}. ${(r.messages || []).slice(0, 2).join(" · ")}`, { duration: 8000 });
      else toast.success(text);
      await load();
    } catch (error) {
      toast.error(zatcaError(error, "Could not send to ZATCA"));
    } finally {
      setSending(false);
    }
  };

  const download = async (r) => {
    const name = `${`${r.entryNumber || "invoice"}`.replace(/[^A-Za-z0-9_-]/g, "_")}_${r.documentType || ""}_ICV${
      r.icv ?? ""
    }.xml`;
    try {
      await downloadZatcaXml(r.salesGuID, name);
    } catch (error) {
      toast.error("Could not download the XML");
    }
  };

  const canSend = (r) => ["UNSIGNED", "SIGNED", "SEND_FAILED"].includes(r.status);

  const columns = [
    {
      title: "Date",
      dataIndex: "invoiceDate",
      render: (v) => (v ? dayjs(v).format("DD MMM YYYY, hh:mm A") : "—"),
      sorter: (a, b) => String(a.invoiceDate || "").localeCompare(String(b.invoiceDate || "")),
    },
    {
      title: "No",
      dataIndex: "entryNumber",
      render: (v) => <b>{v}</b>,
      sorter: (a, b) => String(a.entryNumber || "").localeCompare(String(b.entryNumber || "")),
    },
    {
      title: "Type",
      dataIndex: "documentType",
      render: (v, r) => (v ? DOC_VIEW[v] || v : r.transType === "SR" ? "Return" : "Invoice"),
    },
    { title: "ICV", dataIndex: "icv", align: "right", render: (v) => v ?? "—", sorter: (a, b) => (a.icv || 0) - (b.icv || 0) },
    { title: "Customer", dataIndex: "customerName", render: (v) => v || "—" },
    { title: "Net", dataIndex: "netTotal", align: "right", render: amt },
    { title: "VAT", dataIndex: "taxTotal", align: "right", render: amt },
    {
      title: "Status",
      dataIndex: "status",
      render: (v, r) => {
        const view = STATUS_VIEW[v] || { label: v, tone: "bg-secondary" };
        return (
          <>
            <span className={`badge ${view.tone}`}>{view.label}</span>
            {r.attempts > 1 ? <small> · {r.attempts} tries</small> : null}
          </>
        );
      },
    },
    {
      title: "ZATCA message",
      dataIndex: "responseMsg",
      render: (v) => (
        <span
          title={v || ""}
          style={{ display: "inline-block", maxWidth: 320, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
        >
          {v || "—"}
        </span>
      ),
    },
    {
      title: "",
      key: "actions",
      align: "right",
      render: (_, r) => (
        <span style={{ whiteSpace: "nowrap" }}>
          {canSend(r) ? (
            <button type="button" className="btn btn-sm btn-primary" disabled={sending} onClick={() => send([r.salesGuID])}>
              Send
            </button>
          ) : null}{" "}
          {r.id ? (
            <button type="button" className="btn btn-sm btn-light border" onClick={() => download(r)}>
              XML
            </button>
          ) : null}
        </span>
      ),
    },
  ];

  return (
    <div>
      <LoadingUtils loading={loading || sending} />
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header transfer">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>ZATCA Report</h4>
                <h6>Web sales invoices and returns of {branch?.name || "this branch"} and their ZATCA status</h6>
              </div>
            </div>
            <ul className="table-top-head">
              <li>
                <OverlayTrigger placement="top" overlay={renderTooltip("Refresh")}>
                  <Link onClick={load}>
                    <RotateCcw />
                  </Link>
                </OverlayTrigger>
              </li>
              <li>
                <OverlayTrigger placement="top" overlay={renderTooltip("Collapse")}>
                  <Link
                    id="collapse-header"
                    className={togglehead ? "active" : ""}
                    onClick={() => dispatch(setheadertoggle(!togglehead))}
                  >
                    <ChevronUp />
                  </Link>
                </OverlayTrigger>
              </li>
            </ul>
          </div>

          <div className="row">
            <Stat label={`Signed in ${dayjs(to).year()}`} value={report?.yearTotal ?? 0} />
            <Stat label="Accepted by ZATCA" value={report?.yearDone ?? 0} />
            <Stat label="Waiting to send" value={report?.yearPending ?? 0} />
            <Stat label="Rejected" value={report?.yearRejected ?? 0} />
          </div>

          <div className="card table-list-card">
            <div className="card-body">
              <div className="row align-items-end mb-3">
                <div className="col-md-2">
                  <label className="form-label">From</label>
                  <input type="date" className="form-control" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
                </div>
                <div className="col-md-2">
                  <label className="form-label">To</label>
                  <input type="date" className="form-control" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Status</label>
                  <select className="form-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                    {STATUSES.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Search</label>
                  <input
                    type="search"
                    className="form-control"
                    placeholder="Invoice no, customer, message"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <div className="col-md-2 text-end">
                  <button
                    type="button"
                    className="btn btn-primary w-100"
                    disabled={sending || !branch?.guid}
                    onClick={() => send([])}
                  >
                    Send all pending
                  </button>
                </div>
              </div>

              <div className="table-responsive">
                <Table
                  columns={columns}
                  dataSource={rows}
                  rowKey={(r) => r.salesGuID}
                  className="table datanew dataTable no-footer custom-table"
                  locale={{
                    emptyText:
                      "No invoices match. Invoices appear here once ZATCA is on for the branch (ADMINISTRATION › ZATCA Setup) and they are dated from its start date.",
                  }}
                  pagination={{ showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], defaultPageSize: 20 }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ZatcaReport;
